import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { X, CheckCircle, AlertCircle, Info, Clock, FileText } from 'lucide-react';
import './ReportPopover.css';

function ReportPopover({ jobId, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await api.get(`/jobs/${jobId}/report`);
        setReport(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch report:', err);
        setError(err.response?.data?.detail || 'Nem sikerült betölteni a riportot');
        setLoading(false);
      }
    };

    fetchReport();
  }, [jobId]);

  const getQualityColor = (score) => {
    if (score >= 90) return '#10b981'; // green
    if (score >= 70) return '#f59e0b'; // orange
    return '#ef4444'; // red
  };

  const getQualityLabel = (score) => {
    if (score >= 90) return 'Kiváló';
    if (score >= 70) return 'Megfelelő';
    return 'Javítandó';
  };

  return (
    <div className="report-overlay" onClick={onClose}>
      <div className="report-popover" onClick={(e) => e.stopPropagation()}>
        <div className="report-header">
          <div className="report-title-section">
            <FileText size={24} className="report-icon" />
            <div>
              <h2 className="report-title">Konvertálási riport</h2>
              {report && <p className="report-filename">{report.filename}</p>}
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="report-content">
          {loading && (
            <div className="report-loading">
              <div className="spinner"></div>
              <p>Riport betöltése...</p>
            </div>
          )}

          {error && (
            <div className="report-error">
              <AlertCircle size={48} />
              <p>{error}</p>
            </div>
          )}

          {report && (
            <>
              {/* Summary Section */}
              <div className="report-section">
                <h3 className="section-title">Összefoglaló</h3>
                <div className="summary-grid">
                  <div className="summary-card">
                    <div className="summary-label">Összes sor</div>
                    <div className="summary-value">{report.summary.total_rows}</div>
                  </div>
                  <div className="summary-card success">
                    <div className="summary-label">Sikeres</div>
                    <div className="summary-value">{report.summary.successful_rows}</div>
                  </div>
                  <div className="summary-card error">
                    <div className="summary-label">Sikertelen</div>
                    <div className="summary-value">{report.summary.failed_rows}</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Sikerességi arány</div>
                    <div className="summary-value">{report.summary.success_rate.toFixed(1)}%</div>
                  </div>
                </div>
              </div>

              {/* Quality Score */}
              <div className="report-section">
                <h3 className="section-title">Minőségi pontszám</h3>
                <div className="quality-score-container">
                  <div 
                    className="quality-score-circle"
                    style={{ borderColor: getQualityColor(report.summary.quality_score) }}
                  >
                    <div 
                      className="quality-score-value"
                      style={{ color: getQualityColor(report.summary.quality_score) }}
                    >
                      {report.summary.quality_score.toFixed(1)}
                    </div>
                    <div className="quality-score-label">
                      {getQualityLabel(report.summary.quality_score)}
                    </div>
                  </div>
                  <div className="quality-details">
                    <div className="quality-item">
                      <Clock size={16} />
                      <span>Feldolgozási idő: {report.summary.processing_time_seconds.toFixed(2)}s</span>
                    </div>
                    <div className="quality-item">
                      <AlertCircle size={16} />
                      <span>Figyelmeztetések: {report.summary.warnings_count}</span>
                    </div>
                    <div className="quality-item">
                      <AlertCircle size={16} />
                      <span>Hibák: {report.summary.errors_count}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quality Metrics */}
              {(report.quality_metrics.missing_required_fields > 0 ||
                report.quality_metrics.invalid_dates > 0 ||
                report.quality_metrics.invalid_emails > 0 ||
                report.quality_metrics.missing_translations > 0) && (
                <div className="report-section">
                  <h3 className="section-title">Minőségi metrikák</h3>
                  <div className="metrics-list">
                    {report.quality_metrics.missing_required_fields > 0 && (
                      <div className="metric-item">
                        <AlertCircle size={16} className="metric-icon error" />
                        <span>Hiányzó kötelező mezők: {report.quality_metrics.missing_required_fields}</span>
                      </div>
                    )}
                    {report.quality_metrics.invalid_dates > 0 && (
                      <div className="metric-item">
                        <AlertCircle size={16} className="metric-icon error" />
                        <span>Érvénytelen dátumok: {report.quality_metrics.invalid_dates}</span>
                      </div>
                    )}
                    {report.quality_metrics.invalid_emails > 0 && (
                      <div className="metric-item">
                        <AlertCircle size={16} className="metric-icon error" />
                        <span>Érvénytelen email címek: {report.quality_metrics.invalid_emails}</span>
                      </div>
                    )}
                    {report.quality_metrics.missing_translations > 0 && (
                      <div className="metric-item">
                        <Info size={16} className="metric-icon warning" />
                        <span>Hiányzó fordítások: {report.quality_metrics.missing_translations}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Field Statistics */}
              {report.field_statistics && Object.keys(report.field_statistics).length > 0 && (
                <div className="report-section">
                  <h3 className="section-title">Mező statisztikák</h3>
                  <div className="field-stats-list">
                    {Object.entries(report.field_statistics)
                      .sort((a, b) => (b[1].errors + b[1].warnings) - (a[1].errors + a[1].warnings))
                      .slice(0, 5)
                      .map(([field, stats]) => (
                        <div key={field} className="field-stat-item">
                          <div className="field-name">{field}</div>
                          <div className="field-counts">
                            {stats.errors > 0 && (
                              <span className="field-count error">{stats.errors} hiba</span>
                            )}
                            {stats.warnings > 0 && (
                              <span className="field-count warning">{stats.warnings} figyelmeztetés</span>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {report.recommendations && report.recommendations.length > 0 && (
                <div className="report-section">
                  <h3 className="section-title">Javaslatok</h3>
                  <div className="recommendations-list">
                    {report.recommendations.map((rec, index) => (
                      <div key={index} className="recommendation-item">
                        {rec.startsWith('⚠️') ? (
                          <AlertCircle size={16} className="rec-icon warning" />
                        ) : (
                          <Info size={16} className="rec-icon info" />
                        )}
                        <span>{rec.replace(/^[⚠️ℹ️]\s*/, '')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Success Message */}
              {report.summary.quality_score >= 90 && (
                <div className="success-message">
                  <CheckCircle size={20} />
                  <span>A konverzió kiváló minőségben sikerült!</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportPopover;
